import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { routes } from '@app/routes';
import { CalloutBanner } from '@components/CalloutBanner';
import { Trans, useTrans } from '@libs/i18n';
import { ArrowRightIcon, CopyIcon, SpinnerIcon } from '@phosphor-icons/react';

import { useRemediationScope } from '../api/remediation-api';

interface RemediationCtaBannerProps {
  assessmentId: string;
}

/**
 * Entry point into AI document remediation, shown on a completed assessment
 * (Screen 1).
 *
 * Renders nothing at all unless there is something to offer. The scope endpoint
 * is the single source of truth for that: it 400s when the assessment is not
 * completed, has no results, or has no gaps, and it returns the lineage's
 * `activeRun` when one is already in flight. So this component never duplicates
 * those eligibility rules — it just reacts to the response.
 *
 * Two states:
 * - an active run exists → point at it, do not offer a second one (the backend
 *   would 409 anyway: one run per assessment lineage).
 * - gaps exist → open the pre-flight scope screen. Deliberately not a "start
 *   now" button; nothing should kick off an AI run before the user has seen
 *   what it will produce.
 */
export const RemediationCtaBanner: FC<RemediationCtaBannerProps> = observer(
  ({ assessmentId }) => {
    const navigate = useNavigate();
    const { t } = useTrans();
    const { data: scope, isError } = useRemediationScope(assessmentId);

    // Not eligible, still loading, or the request failed — stay out of the way.
    // A banner that flickers in after the page settles is worse than no banner.
    if (isError || !scope) return null;

    const missing = scope.generate.length;
    const weak = scope.remediate.length;
    if (missing === 0 && weak === 0) return null;

    const activeRun = scope.activeRun;

    // Assessed before remediation shipped (the server decides this; see the
    // cutoff in GetRemediationScopeService). The gaps are real, but the stored
    // text came from the older extractor and remediation cannot anchor its edits
    // to it reliably — so point the user at duplicating the assessment rather
    // than starting a run that would produce poor output. Suppressed once a run
    // is in flight; that run's own screens report its state and this banner
    // should not contradict them.
    if (!activeRun && scope.legacyExtraction) {
      return (
        <CalloutBanner
          tone="warning"
          icon={CopyIcon}
          title={<Trans i18nKey="app.remediation.cta.legacyTitle" />}
          action={
            <Button
              variant="secondary"
              endIcon={<ArrowRightIcon size={18} />}
              onClick={() => navigate(routes.assessmentList())}
            >
              {t('app.remediation.cta.legacyAction')}
            </Button>
          }
        >
          <Trans i18nKey="app.remediation.cta.legacyBody" />
        </CalloutBanner>
      );
    }

    const bodyKey =
      missing > 0 && weak > 0
        ? 'app.remediation.cta.body'
        : missing > 0
          ? 'app.remediation.cta.bodyGenerateOnly'
          : 'app.remediation.cta.bodyRemediateOnly';

    return (
      <CalloutBanner
        tone="ai"
        filledIcon={!activeRun}
        icon={activeRun ? SpinnerIcon : undefined}
        title={
          <Trans
            i18nKey={activeRun ? 'app.remediation.cta.inProgress' : 'app.remediation.cta.title'}
          />
        }
        action={
          <Button
            variant={activeRun ? 'secondary' : 'ai'}
            endIcon={<ArrowRightIcon size={18} />}
            onClick={() =>
              navigate(
                activeRun
                  ? routes.remediationRun(activeRun.runId)
                  : routes.remediationScope(assessmentId),
              )
            }
          >
            {t(activeRun ? 'app.remediation.cta.viewRun' : 'app.remediation.cta.action')}
          </Button>
        }
      >
        {activeRun ? (
          <Trans i18nKey="app.remediation.cta.inProgressBody" />
        ) : (
          <Trans i18nKey={bodyKey} values={{ missing, weak }} />
        )}
      </CalloutBanner>
    );
  },
);
